import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { CorrespondenceAddress, FormType } from '../types';
import { CheckBox, Radio, Select, TextFiled } from 'tsx/comps/form';
import { get } from 'lodash';
import { countries } from '../data';

const Div = styled.div`
  .subtitle {
    display: block;
    font-size: 14px;
    opacity: 0.6;
    font-style: italic;
  }
`;

interface Step1Props {
  onNext: () => void;
}
const allowedSignaturesMapping: { [key: string]: number } = {
  One: 0,
  'Both/all': 1,
};

export const Step1: React.FC<Step1Props> = ({ onNext }) => {
  const { values, handleChange, handleBlur, touched, errors, setFieldValue, validateField, setFieldTouched } =
    useFormikContext<FormType>();
  const { interestRequirement, ...newValues } = values;

  const withdrawalSignatures = values.accountOpeningDetails.jointAccountDetails?.withdrawalSignatures || ''; // Default to empty string if jointAccountDetails is null
  const allowedSignaturesNumeric = allowedSignaturesMapping[withdrawalSignatures];

  const validate = () => {
    const timer = setTimeout(() => {
      validateField('accountOpeningDetails');
      clearTimeout(timer);
    }, 1);
  };

  return (
    <Div>
      <h2>Account Opening Details</h2>
      <div className="spc30" />
      <div className="input-grid">
        <div>
          <span className="label">I/We would like to invest (amount)</span>
          <div className="input-endorment">
            <TextFiled
              id="accountOpeningDetails.investmentAmount"
              placeholder="Amount"
              type="number"
              $fullWidth
              $withEndormentEnd
              value={values.accountOpeningDetails.investmentAmount}
              onBlur={handleBlur}
              onChange={handleChange}
              error={
                touched.accountOpeningDetails?.investmentAmount && !!errors.accountOpeningDetails?.investmentAmount
              }
            />
            <div className="input-endorment__content">£</div>
          </div>
        </div>
        <div>
          <span className="label">How much do you intend to deposit into this account in the future?</span>
          <div className="input-endorment">
            <TextFiled
              id="accountOpeningDetails.intendedDepositAmount"
              placeholder="Amount"
              type="number"
              $fullWidth
              $withEndormentEnd
              value={values.accountOpeningDetails.intendedDepositAmount}
              onBlur={handleBlur}
              onChange={handleChange}
              error={
                touched.accountOpeningDetails?.intendedDepositAmount &&
                !!errors.accountOpeningDetails?.intendedDepositAmount
              }
            />
            <div className="input-endorment__content">£</div>
          </div>
        </div>
      </div>
      <div className="spc30" />
      <div className="input-grid">
        <div>
          <span className="label">Source of initial deposit</span>
          <TextFiled
            id="accountOpeningDetails.initialDepositSource"
            placeholder="e.g. inheritance, savings, pension"
            $fullWidth
            value={values.accountOpeningDetails.initialDepositSource}
            onBlur={handleBlur}
            onChange={handleChange}
            error={
              touched.accountOpeningDetails?.initialDepositSource &&
              !!errors.accountOpeningDetails?.initialDepositSource
            }
          />
        </div>
        <div>
          <span className="label">Intended purpose of account</span>
          <TextFiled
            id="accountOpeningDetails.intendedAccountPurpose"
            $fullWidth
            value={values.accountOpeningDetails.intendedAccountPurpose}
            onBlur={handleBlur}
            onChange={handleChange}
            error={
              touched.accountOpeningDetails?.intendedAccountPurpose &&
              !!errors.accountOpeningDetails?.intendedAccountPurpose
            }
          />
        </div>
      </div>
      <div className="spc30" />
      <span className="label">Would you like a joint account?</span>
      <div className="radio-container">
        <div className="radio-container__item">
          <Radio
            checked={!!values.accountOpeningDetails.jointAccountDetails}
            onChange={() => {
              setFieldValue('accountOpeningDetails.jointAccountDetails', {
                accountHoldersRelationship: '',
                withdrawalSignatures: '',
                correspondenceAddress: {
                  value: '',
                  otherAddress: null,
                },
              });
              setFieldValue('secondAccountHolderPersonalDetails', {
                existingCustomer: false,
                title: {
                  value: '',
                  otherValue: '',
                },
                firstName: '',
                lastName: '',
                contactNumber: '',
                alternativeContactNumber: '',
                emailAddress: '',
                nationality: '',
                countryOfBirth: '',
                dateOfBirth: '',
                currentAddress: {
                  line1: '',
                  line2: '',
                  city: '',
                  county: '',
                  postcode: '',
                  country: '',
                },
                preferredCorrespondence: '',
                preferredContactTime: '',
                usCitizen: {
                  value: null,
                  usTaxIdentificationNumber: '',
                },
                taxOtherUK: {
                  value: null,
                  details: '',
                },
                employmentStatus: {
                  employmentStatus: '',
                  occupation: '',
                  annualIncome: '',
                  // seniorPositionDetails: {
                  //   value: null,
                  //   details: '',
                  // },
                  seniorPositionDetails: '',
                },
              });
              validate();
            }}
          />
          <p className="radio-container__item__label">Yes</p>
        </div>
        <div className="radio-container__item">
          <Radio
            checked={!values.accountOpeningDetails.jointAccountDetails}
            onChange={() => {
              setFieldTouched('accountOpeningDetails.jointAccountDetails', false);
              setFieldValue('accountOpeningDetails.jointAccountDetails', null);
              setFieldValue('secondAccountHolderPersonalDetails', null);
              validate();
            }}
          />
          <p className="radio-container__item__label">No</p>
        </div>
      </div>
      {values.accountOpeningDetails.jointAccountDetails && (
        <>
          <div className="spc30" />
          <div className="input-grid">
            <div>
              <span className="label">What is your relationship between the account holders?</span>
              <TextFiled
                id="accountOpeningDetails.jointAccountDetails.accountHoldersRelationship"
                placeholder="e.g. inheritance, savings, pension"
                $fullWidth
                value={values.accountOpeningDetails.jointAccountDetails.accountHoldersRelationship}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  get(touched, 'accountOpeningDetails.jointAccountDetails.accountHoldersRelationship') &&
                  get(errors, 'accountOpeningDetails.jointAccountDetails.accountHoldersRelationship')
                }
              />
            </div>
          </div>
          <div className="spc30" />
          <div className="spc30" />
          <h5>Withdrawal Instructions</h5>
          <div className="spc30" />
          <span className="label">Withdrawals allowed on signatures of:</span>
          <div className="spc10" />
          <div className="spc10" />
          <div className="radio-container radio-container--column">
            <div className="radio-container__item">
              <Radio
                checked={values.accountOpeningDetails.jointAccountDetails.withdrawalSignatures === '0'}
                onChange={() => {
                  setFieldValue('accountOpeningDetails.jointAccountDetails.withdrawalSignatures', '0' || null);
                  validate();
                }}
              />
              <p className="radio-container__item__label">One Signature</p>
            </div>
            <div className="radio-container__item">
              <Radio
                checked={values.accountOpeningDetails.jointAccountDetails.withdrawalSignatures === '1'}
                onChange={() => {
                  setFieldValue('accountOpeningDetails.jointAccountDetails.withdrawalSignatures', '1' || null);
                  validate();
                }}
              />
              <p className="radio-container__item__label">Both/all signatures</p>
            </div>
          </div>
          <span className="subtitle">
            (Refer to Savings Account General Terms & Conditions for signature requirements)
          </span>
          <div className="spc30" />
          <span className="label">Correspondence Address</span>
          <div className="radio-container radio-container--column">
            <div className="radio-container__item">
              <Radio
                checked={
                  values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.value ===
                  CorrespondenceAddress.Applicant1
                }
                onChange={() => {
                  setFieldTouched(
                    'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress',
                    false,
                  );
                  setFieldValue(
                    'accountOpeningDetails.jointAccountDetails.correspondenceAddress.value',
                    CorrespondenceAddress.Applicant1,
                  );
                  setFieldValue('accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress', null);
                  validate();
                }}
              />
              <p className="radio-container__item__label">Applicant 1 current address</p>
            </div>
            <div className="radio-container__item">
              <Radio
                checked={
                  values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.value ===
                  CorrespondenceAddress.Applicant2
                }
                onChange={() => {
                  setFieldTouched(
                    'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress',
                    false,
                  );
                  setFieldValue(
                    'accountOpeningDetails.jointAccountDetails.correspondenceAddress.value',
                    CorrespondenceAddress.Applicant2,
                  );
                  setFieldValue('accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress', null);
                  validate();
                }}
              />
              <p className="radio-container__item__label">Applicant 2 current address</p>
            </div>
            <div className="radio-container__item">
              <Radio
                checked={
                  values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.value ===
                  CorrespondenceAddress.Other
                }
                onChange={() => {
                  setFieldValue(
                    'accountOpeningDetails.jointAccountDetails.correspondenceAddress.value',
                    CorrespondenceAddress.Other,
                  );
                  setFieldValue('accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress', {
                    line1: '',
                    line2: '',
                    city: '',
                    county: '',
                    postcode: '',
                    country: '',
                  });
                  validate();
                }}
              />
              <p className="radio-container__item__label">Other</p>
            </div>
          </div>
          {values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.value ===
            CorrespondenceAddress.Other && (
            <>
              <div className="spc10" />
              <div className="spc10" />
              <p>Add correspondence currentAddress</p>
              <div className="spc10" />
              <TextFiled
                id="accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.line1"
                placeholder="Address line 1"
                $fullWidth
                value={values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress?.line1}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  get(touched, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.line1') &&
                  get(errors, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.line1')
                }
              />
              <div className="spc10" />
              <div className="spc10" />
              <TextFiled
                id="accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.line2"
                placeholder="Address line 2"
                $fullWidth
                value={values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress?.line2}
                onBlur={handleBlur}
                onChange={handleChange}
                error={
                  get(touched, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.line2') &&
                  get(errors, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.line2')
                }
              />
              <div className="spc10" />
              <div className="spc10" />
              <div className="input-grid">
                <TextFiled
                  id="accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.city"
                  placeholder="City"
                  $fullWidth
                  value={values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress?.city}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={
                    get(touched, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.city') &&
                    get(errors, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.city')
                  }
                />
                <TextFiled
                  id="accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.county"
                  placeholder="County"
                  $fullWidth
                  value={values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress?.county}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={
                    get(
                      touched,
                      'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.county',
                    ) &&
                    get(errors, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.county')
                  }
                />
              </div>
              <div className="spc10" />
              <div className="spc10" />
              <div className="input-grid">
                <TextFiled
                  id="accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.postcode"
                  placeholder="Postcode"
                  $fullWidth
                  value={values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress?.postcode}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  error={
                    get(
                      touched,
                      'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.postcode',
                    ) &&
                    get(errors, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.postcode')
                  }
                />
                <Select
                  id="accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.country"
                  value={values.accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress?.country}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={
                    get(
                      touched,
                      'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.country',
                    ) &&
                    get(errors, 'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.country')
                  }
                  $hasValue={get(
                    values,
                    'accountOpeningDetails.jointAccountDetails.correspondenceAddress.otherAddress.country',
                  )}
                >
                  <option value="" disabled selected>
                    Country
                  </option>
                  {countries.map((el) => (
                    <option key={el.name} value={el.name}>
                      {el.name}
                    </option>
                  ))}
                </Select>
              </div>
            </>
          )}
        </>
      )}
      <div className="spc30" />
      <div className="spc30" />
      <div className="radio-container radio-container--column">
        <div className="radio-container__item">
          <CheckBox
            checked={values.accountOpeningDetails.applyToOpenAccount}
            onSelect={(val) => setFieldValue('accountOpeningDetails.applyToOpenAccount', val)}
          />
          <p className="radio-container__item__label">I/We apply to open a Account</p>
        </div>
      </div>

      <div className="spc30" />
      <div className="spc10" />
      <div className="buttons">
        <button className="btn" onClick={onNext} disabled={!!errors.accountOpeningDetails}>
          Next
        </button>
      </div>
    </Div>
  );
};
